import { IList } from 'annotation-tools/utils';
import React, { CSSProperties, memo, useMemo, useState } from 'react';
import styled from 'styled-components';
import { IBboxType, IdType, ILabelType } from '../../imports/annotation-canvas';
import { Percent } from '../../imports/utils';
import { AnnotationItemList } from './annotation-item-list/annotation-item-list';
import { LabelButtons } from './label-buttons/label-buttons';

const Root = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: rgb(245, 245, 245);
`;

const LabelButtonsWrapper = styled.div`
  width: 100%;
  padding: 10px;
  flex-grow: 1;
  flex-shrink: 1;
  overflow-y: auto;
`;

const BboxListWrapper = styled.div`
  width: 100%;
  padding: 10px;
  flex-grow: 1;
  flex-shrink: 1;
  overflow-y: auto;
`;

export const Sidebar = memo(
  ({
    labels,
    labelSaturation,
    labelLightness,
    visibleLabels,
    selectedLabel,
    bboxesRelative,
    selectedId,
    highlightId,
    onVisibilityClick,
    onLabelClick,
    onShowAllClick,
    onHideAllClick,
    onSelectedIdChange,
    onHighlightIdChange,
    disabled
  }: Readonly<{
    labels: IList<ILabelType>;
    labelSaturation: Percent;
    labelLightness: Percent;
    bboxesRelative: IList<IBboxType>;
    selectedId: IdType | undefined;
    highlightId: IdType | undefined;
    visibleLabels: IList<ILabelType>;
    selectedLabel: ILabelType | undefined;
    onSelectedIdChange: (id: IdType | undefined) => void;
    onHighlightIdChange: (id: IdType | undefined) => void;
    onLabelClick: (id: IdType) => void;
    onVisibilityClick: (id: IdType) => void;
    onShowAllClick: () => void;
    onHideAllClick: () => void;
    disabled: boolean;
  }>) => {
    const [labelsHidden, setLabelsHidden] = useState(false);
    const [bboxListHidden, setBboxListHidden] = useState(false);

    const labelButtonsWrapperStyle = useMemo<CSSProperties>(
      () => (labelsHidden ? { maxHeight: 'min-content' } : {}),
      [labelsHidden]
    );

    const bboxListWrapperStyle = useMemo<CSSProperties>(
      () => (bboxListHidden ? { maxHeight: 'min-content' } : {}),
      [bboxListHidden]
    );

    return (
      <Root>
        <LabelButtonsWrapper style={labelButtonsWrapperStyle}>
          <LabelButtons
            labels={labels}
            labelSaturation={labelSaturation}
            labelLightness={labelLightness}
            visibleLabels={visibleLabels}
            onLabelClick={onLabelClick}
            onVisibilityClick={onVisibilityClick}
            onShowAllClick={onShowAllClick}
            onHideAllClick={onHideAllClick}
            selectedLabel={selectedLabel}
            hidden={labelsHidden}
            onHiddenChange={setLabelsHidden}
            disabled={disabled}
          />
        </LabelButtonsWrapper>
        <BboxListWrapper style={bboxListWrapperStyle}>
          <AnnotationItemList
            bboxes={bboxesRelative}
            selectedId={selectedId}
            highlightId={highlightId}
            onClick={onSelectedIdChange}
            onHover={onHighlightIdChange}
            hidden={bboxListHidden}
            onHiddenChange={setBboxListHidden}
            disabled={disabled}
          />
        </BboxListWrapper>
      </Root>
    );
  }
);

Sidebar.displayName = 'Sidebar';
